import React, { useEffect, useRef, useState } from 'react';

interface Props {
  file: File;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
  /** Output (and viewport) is a square; this is the exported pixel size. */
  outputSize?: number;
}

const VIEWPORT = 288; // on-screen crop square (px)

/**
 * Dependency-free square avatar cropper. The image covers the viewport and can
 * be panned (drag) and zoomed (slider); what you see in the square is exactly
 * what gets exported, so there are no surprises after upload.
 */
export const AvatarCropper: React.FC<Props> = ({ file, onCancel, onConfirm, outputSize = 256 }) => {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const baseScale = useRef(1); // scale that makes the image just cover the viewport
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const clamp = (o: { x: number; y: number }, eff: number, iw: number, ih: number) => ({
    x: Math.min(0, Math.max(VIEWPORT - iw * eff, o.x)),
    y: Math.min(0, Math.max(VIEWPORT - ih * eff, o.y)),
  });

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      baseScale.current = Math.max(VIEWPORT / im.width, VIEWPORT / im.height);
      const eff = baseScale.current;
      setImg(im);
      setOffset({ x: (VIEWPORT - im.width * eff) / 2, y: (VIEWPORT - im.height * eff) / 2 });
    };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!img) return;
    const eff = baseScale.current * zoom;
    setOffset((o) => clamp(o, eff, img.width, img.height));
  }, [zoom, img]);

  const eff = img ? baseScale.current * zoom : 1;

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !img) return;
    setOffset(
      clamp(
        { x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) },
        eff, img.width, img.height,
      ),
    );
  };
  const onPointerUp = () => { drag.current = null; };

  const confirm = () => {
    if (!img) return;
    setSaving(true);
    const s = VIEWPORT / eff;            // source square size in image pixels
    const sx = -offset.x / eff;
    const sy = -offset.y / eff;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) { setSaving(false); return; }
    ctx.drawImage(img, sx, sy, s, s, 0, 0, outputSize, outputSize);
    canvas.toBlob((b) => (b ? onConfirm(b) : setSaving(false)), 'image/jpeg', 0.85);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <div className="bg-card rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold text-foreground">Adjust your photo</h3>
        <p className="text-sm text-muted-foreground mb-4">Drag to reposition, slide to zoom.</p>

        <div
          className="relative mx-auto rounded-xl overflow-hidden bg-muted select-none cursor-move"
          style={{ width: VIEWPORT, height: VIEWPORT, touchAction: 'none' }}
          onPointerDown={onPointerDown}
        >
          {img && (
            <img
              src={img.src}
              alt=""
              draggable={false}
              style={{ position: 'absolute', left: offset.x, top: offset.y, width: img.width * eff, height: img.height * eff, maxWidth: 'none' }}
            />
          )}
          <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/10" />
        </div>

        <input
          type="range" min={1} max={3} step={0.01} value={zoom}
          onChange={(e) => setZoom(parseFloat(e.target.value))}
          className="w-full mt-4 accent-orange-500" aria-label="Zoom"
        />

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted">Cancel</button>
          <button
            type="button" onClick={confirm} disabled={!img || saving}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarCropper;
